/**
 * `SuccessAnimations` — what the win panel shows between its title and its
 * button, and when.
 *
 * Decomp read for this file:
 *   bustraffic_kit/code/decomp_game/SuccessAnimations$$Show__0x21407a8.c
 *   bustraffic_kit/code/decomp_game/SuccessAnimations$$Hide__0x21411c8.c
 *   bustraffic_kit/code/decomp_game/SuccessAnimations$$GetFeatureUnlockProgress__0x21410ac.c
 *   bustraffic_kit/code/decomp_game/SuccessAnimations$$_HasNewFeature_b__24_0__0x21413fc.c
 *   bustraffic_kit/code/decomp_game/SuccessAnimations$$_GetFeatureUnlockData_b__25_0__0x2141424.c
 *   bustraffic_kit/code/decomp_game/SuccessAnimations.__c__DisplayClass22_0$$_Show_b__0__0x214144c.c
 *   bustraffic_kit/code/decomp_game/SuccessModel$$.ctor__0x2141820.c
 *
 * ## The feature bar
 *
 * `FeatureDataSO.data` is a `List<FeatureUnlockInfo> { type, icon, title,
 * unlockLevel, … }` and the shipped `Resources/features/FeatureDataSet.asset`
 * is a 402-byte stub — the list did not survive the export. What did survive is
 * the arithmetic over it:
 *
 *     GetFeatureUnlockData():                          // b__25_0
 *         data.FirstOrDefault(f => userLevel + 1 <= f.unlockLevel)
 *     HasNewFeature():                                 // b__24_0, same predicate
 *         data.Any(f => userLevel + 1 <= f.unlockLevel)
 *     GetFeatureUnlockProgress():
 *         f = GetFeatureUnlockData(); if (f == null) return (0, 0);
 *         i = data.IndexOf(f);
 *         prev = i < 1 ? 1 : data[i - 1].unlockLevel;
 *         return (userLevel - prev + 1, f.unlockLevel - prev);
 *     Show():
 *         featureProgressText = $"{a - 1}/{b}";
 *         featureProgressImage.fillAmount = (a - 1) / b;
 *         …AppendInterval(2.5f).Append(DOFillAmount(a / b, 0.6f)).OnComplete(b__2)
 *     <Show>b__2():                                    // DisplayClass22_1
 *         featureProgressText = $"{a}/{b}";
 *         if (a != b) return;
 *         _featureSubSequence = Sequence()
 *             .Append(featureIconImage.DOScale(1.1f, 0.2f))
 *             .Append(featureIconImage.DOScale(1f, 0.2f))
 *             .Join(featureGlowImage.DOFade(1f, 0.2f))
 *             .OnComplete(b__3)
 *     <Show>b__3():  featureProgressText = <literal>     // ❓ lost
 *
 * So the caption lags the bar by one until the tween lands, then catches up;
 * and a full bar is what switches the glow on — it is invisible until then
 * (the shipped 4/7 screenshot has no glow) — pops the icon, and swaps the
 * caption for a word that did not survive.
 *
 * and `userLevel` is `SuccessModel._completedLevelIndex`, copied out of
 * `LevelService + 0x10` in the model's constructor.
 *
 * ### 📐 The list, recovered from the 40 levels and one screenshot
 *
 * Three things unlock over the ladder, and each first appears in exactly one
 * level of `web/public/data/levels/`: a `boxType: 1` (Hidden) bus in level 7,
 * `ElevatorData` in level 14, a `boxType: 2` (Ice) bus in level 24. The
 * screenshot of the shipped panel after **level 10** shows the lift icon
 * (`Lift.png`) over `4/7` **with the Continue button already up** — Continue
 * scales in at 3.0 s and the fill lands at 3.1 s, so that caption is `b__2`'s
 * landed `a/b`, not the opening `a - 1`. `14 - 7 = 7` is the only pair of those
 * numbers that gives the 7, and `a = 4` then gives `userLevel = 10`. So:
 *
 *  - the list is ordered by `unlockLevel` — Hidden, Lifts, Ice — and
 *  - `userLevel` is the number of the level just completed.
 *
 * Which also makes the bar behave: after 13 it runs 6/7 -> 7/7, unlocks, and
 * level 14 is the first with lifts. (A first reading took `4/7` for the
 * opening caption, got `userLevel = 11`, and had the bar full after 12 and
 * already on ice after 13 — the player noticed.)
 *
 * `successFeature.test.ts` re-derives the three levels from the level files and
 * pins the screenshot's `4/7`.
 *
 * ## The timeline
 *
 * `Show` is not `BasePopupAnimations.ShowAnimation` — nothing scales the frame:
 *
 *     seq.AppendInterval(1f)
 *        .Append(overlayCanvasGroup.DOFade(1, 0.3f)
 *                .OnComplete(() => { textAnimator.Play(); confetti.DoPlay(); }))
 *        .Insert(2f, contentCanvasGroup.DOFade(1, 0.4f))
 *        .Insert(3f, continueWithRWContainer.DOScale(1, 0.25f))
 *        .Insert(3f, continueContainer.DOScale(1, 0.25f))
 *     Hide: overlayCanvasGroup.DOFade(0, 0.2f)
 *
 * With no feature left to unlock, `CoinPanel` bobs instead:
 *
 *     _noFeatureSequence.AppendInterval(1f)
 *        .Append(coinPanel.DOLocalMoveY(10f, 2f)).Join(coinPanel.DOScale(1.2f, 2f))
 *        .SetLoops(-1, LoopType.Yoyo)
 *
 * `completeParticles[]` are played first thing, each with `SoundService.Play(1)`
 * (Confetti) — `main.ts` already plays that once. ❓ Every `SetEase`.
 */

export const enum FeatureUnlockType {
  HiddenBus = 0,
  IceBus = 1,
  Lifts = 2,
}

export interface FeatureUnlockInfo {
  type: FeatureUnlockType
  /** The `Sprite` on `FeatureIcon` — matched to the screenshot's lift. */
  icon: string
  /** 📐 First level of `web/public/data/levels/` that carries the feature. */
  unlockLevel: number
}

/** 📐 `FeatureDataSO.data`, ordered by `unlockLevel` — see the header. */
export const FEATURE_DATA: readonly FeatureUnlockInfo[] = [
  { type: FeatureUnlockType.HiddenBus, icon: 'HiddenBus.png', unlockLevel: 7 },
  { type: FeatureUnlockType.Lifts, icon: 'Lift.png', unlockLevel: 14 },
  { type: FeatureUnlockType.IceBus, icon: 'IceBus.png', unlockLevel: 24 },
]

/** `SuccessModel._completedLevelIndex` — the level just completed (📐, see above). */
export function successUserLevel(completedLevel: number): number {
  return completedLevel
}

/** `GetFeatureUnlockData` — the first feature still ahead of the player. */
export function featureUnlockData(
  userLevel: number,
  data: readonly FeatureUnlockInfo[] = FEATURE_DATA,
): FeatureUnlockInfo | null {
  return data.find((f) => userLevel + 1 <= f.unlockLevel) ?? null
}

/**
 * What the bar says: `a - 1` of `b` while it fills, `a` of `b` once it has,
 * and — when that is full — the unlock word.
 */
export function featureBarCaptions(a: number, b: number): { before: string; after: string; full: boolean } {
  return { before: `${a - 1}/${b}`, after: `${a}/${b}`, full: a === b }
}

/** `GetFeatureUnlockProgress` — `(a, b)`; the panel prints `a - 1` of `b`. */
export function featureUnlockProgress(
  userLevel: number,
  data: readonly FeatureUnlockInfo[] = FEATURE_DATA,
): [number, number] {
  const f = featureUnlockData(userLevel, data)
  if (!f) return [0, 0]
  const i = data.indexOf(f)
  const prev = i < 1 ? 1 : data[i - 1].unlockLevel
  return [userLevel - prev + 1, f.unlockLevel - prev]
}

/** 🔍 `SuccessAnimations.Show` / `Hide` — seconds. */
export const SUCCESS_ANIM = {
  /** `AppendInterval(1f)` before anything is visible. */
  leadIn: 1.0,
  /** `overlayCanvasGroup.DOFade(1, 0.3f)`. */
  overlayFade: 0.3,
  /** `Insert(2f, contentCanvasGroup.DOFade(1, 0.4f))`. */
  contentAt: 2.0,
  contentFade: 0.4,
  /** `Insert(3f, continueContainer.DOScale(1, 0.25f))`. */
  buttonsAt: 3.0,
  buttonsScale: 0.25,
  /** `_featureSequence`: `AppendInterval(2.5f)` then `DOFillAmount(…, 0.6f)`. */
  fillAt: 2.5,
  fillTween: 0.6,
  /** `b__2` on a full bar: icon `DOScale(1.1, 0.2)` then `(1, 0.2)`, glow `DOFade(1, 0.2)`. */
  fullIconPop: 0.2,
  fullGlowFade: 0.2,
  /** `_noFeatureSequence`: 1 s in, then 2 s up (+10, ×1.2) and 2 s back, forever. */
  coinBobAt: 1.0,
  coinBobHalf: 2.0,
  coinBobLift: 10,
  coinBobScale: 1.2,
  /** `Hide`: `overlayCanvasGroup.DOFade(0, 0.2f)`. */
  hideFade: 0.2,
}
